import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";

import { fetchAccountBillingRates, fetchAccountPlanMap } from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import {
    accountFromSearchParams,
    money,
    toIsoDate,
    writeAccountParams,
    writePlanParams,
} from "./financeShared";
import "./BillingRates.scss";

const planOptionLabel = (plan) => {
    const name = plan?.name || "Plan";
    const hcc = plan?.hccId ? ` (${plan.hccId})` : "";
    const status = plan?.statusLabel ? ` · ${plan.statusLabel}` : "";
    return `${name}${hcc}${status}`;
};

const versionRange = (eff, exp) => {
    if (!eff) return "—";
    const expYear = Number(String(exp || "").slice(0, 4));
    const open = !exp || Number.isNaN(expYear) || expYear >= 2999;
    return open ? `${eff} → open` : `${eff} → ${exp}`;
};

const amount = (rate, percentage) => {
    if (rate != null) return money(rate);
    if (percentage != null) return `${percentage}%`;
    return "—";
};

const selectMenuStyles = {
    ...customStyles,
    menuPortal: (base) => ({ ...base, zIndex: 200 }),
};

const filterPlanRates = (plan, needle) => {
    const planHit = [plan.planName, plan.planHccId, plan.statusLabel]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    if (planHit) return plan;
    const categories = (plan.categories || [])
        .map((cat) => {
            const catHit = [cat.name, cat.code].join(" ").toLowerCase().includes(needle);
            const components = (cat.components || []).filter((comp) =>
                [comp.name, comp.method].join(" ").toLowerCase().includes(needle),
            );
            if (!catHit && !components.length) return null;
            return { ...cat, components: catHit ? cat.components : components };
        })
        .filter(Boolean);
    if (!categories.length) return null;
    return { ...plan, categories };
};

const BillingRates = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [account, setAccount] = useState(() => accountFromSearchParams(searchParams));
    const [planOption, setPlanOption] = useState(null);
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [q, setQ] = useState("");
    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;
    const planKey = planOption?.value != null ? Number(planOption.value) : null;
    const planKeyParam = searchParams.get("planKey");

    useEffect(() => {
        const next = accountFromSearchParams(searchParams);
        if (next?.additional?.accountKey !== account?.additional?.accountKey) {
            setAccount(next);
            setPlanOption(null);
            setQ("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("accountKey")]);

    useEffect(() => {
        setPlanOption(null);
        setQ("");
    }, [accountKey]);

    const {
        data: planMap,
        isLoading: mapLoading,
        error: mapError,
    } = useQuery({
        queryKey: ["account-plan-map", accountKey, asOf],
        queryFn: () => fetchAccountPlanMap({ accountKey, asOfDate: asOf }),
        enabled: !!accountKey && !!asOf,
        staleTime: 0,
        retry: false,
    });

    const planOptions = useMemo(() => {
        const byKey = new Map();
        for (const acct of planMap?.accounts || []) {
            for (const p of acct.plans || []) {
                if (p.key == null || byKey.has(Number(p.key))) continue;
                byKey.set(Number(p.key), {
                    value: Number(p.key),
                    label: planOptionLabel(p),
                    additional: p,
                });
            }
        }
        return [...byKey.values()].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
    }, [planMap]);

    useEffect(() => {
        if (!planOption) return;
        if (mapLoading) return;
        if (!planOptions.some((o) => Number(o.value) === Number(planOption.value))) {
            setPlanOption(null);
            setSearchParams((prev) => writePlanParams(prev, null), { replace: true });
        }
    }, [planOptions, planOption, mapLoading, setSearchParams]);

    useEffect(() => {
        if (!planKeyParam || !planOptions.length) return;
        if (Number(planOption?.value) === Number(planKeyParam)) return;
        const match = planOptions.find((o) => Number(o.value) === Number(planKeyParam));
        if (match) setPlanOption(match);
    }, [planKeyParam, planOptions, planOption]);

    const { data, isLoading, error } = useQuery({
        queryKey: ["account-billing-rates", accountKey || null, asOf],
        queryFn: () => fetchAccountBillingRates({ accountKey, asOfDate: asOf }),
        enabled: !!accountKey,
        staleTime: 30_000,
        retry: false,
    });

    const rates = data?.rates || [];
    const forPlan = useMemo(() => {
        if (planKey == null) return [];
        return rates.filter((plan) => Number(plan.planKey) === planKey);
    }, [rates, planKey]);

    const needle = q.trim().toLowerCase();
    const filtered = useMemo(() => {
        if (!needle) return forPlan;
        return forPlan.map((plan) => filterPlanRates(plan, needle)).filter(Boolean);
    }, [forPlan, needle]);

    const setAccountAndParams = (opt) => {
        setAccount(opt);
        setPlanOption(null);
        setQ("");
        const next = writePlanParams(writeAccountParams(searchParams, opt), null);
        setSearchParams(next, { replace: true });
    };

    const setPlanAndParams = (opt) => {
        setPlanOption(opt);
        setSearchParams(writePlanParams(searchParams, opt), { replace: true });
    };

    const clearFilters = () => {
        setAccount(null);
        setPlanOption(null);
        setAsOfDate(new Date());
        setQ("");
        setSearchParams(writePlanParams(writeAccountParams(searchParams, null), null), {
            replace: true,
        });
    };

    const mapFailed = mapError || planMap?.error;

    return (
        <div className="container billing-rates">
            <div className="billing-rates__schedule-head">
                <div>
                    <p className="title">Billing rates</p>
                    <p className="sub-title">
                        What the employer owes per plan as of a date. Rates feed the premium bill —
                        they are not claim payables.
                    </p>
                </div>
                <div className="billing-rates__schedule-actions">
                    <Link className="billing-rates__link-btn" to="/finance/billing-rate-builder">
                        Open rate builder
                    </Link>
                </div>
            </div>

            <div className="billing-rates__filters">
                <div className="billing-rates__field billing-rates__field--account">
                    <label>Account</label>
                    <AsyncPaginate
                        placeholder="Select an account"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={setAccountAndParams}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={selectMenuStyles}
                        isClearable
                    />
                </div>
                {account && (
                    <div className="billing-rates__field billing-rates__field--plan">
                        <label>Benefit plan</label>
                        <Select
                            placeholder={
                                mapLoading
                                    ? "Loading plans…"
                                    : planOptions.length
                                      ? "Select a benefit plan"
                                      : "No plans for this as-of date"
                            }
                            className="react-select-container"
                            classNamePrefix="react-select"
                            value={planOption}
                            onChange={setPlanAndParams}
                            options={planOptions}
                            isDisabled={mapLoading || !planOptions.length}
                            isLoading={mapLoading}
                            isClearable
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={selectMenuStyles}
                        />
                    </div>
                )}
                <div className="billing-rates__field billing-rates__field--date">
                    <label>As of</label>
                    <DatePicker
                        selected={asOfDate}
                        onChange={(d) => setAsOfDate(d || new Date())}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="As of date"
                        todayButton="Today"
                        className="date-picker"
                        wrapperClassName="date-picker-wrapper"
                        portalId="root"
                    />
                </div>
                <button type="button" className="billing-rates__clear-btn danger" onClick={clearFilters}>
                    Clear filters
                </button>
            </div>

            {!accountKey && (
                <p className="billing-rates__hint">
                    Select an account, then a benefit plan effective on the as-of date.
                </p>
            )}
            {accountKey && mapFailed && (
                <p className="danger">
                    {mapError?.message || planMap?.error || "Failed to load account plans"}
                </p>
            )}
            {accountKey && !mapLoading && !mapFailed && !planOptions.length && (
                <p className="billing-rates__hint">
                    No benefit plans for this account on the selected as-of date.
                </p>
            )}
            {accountKey && planOptions.length > 0 && !planKey && (
                <p className="billing-rates__hint">Select a benefit plan to load its rates.</p>
            )}

            {planKey && (
                <>
                    <div className="billing-rates__table-filters">
                        <input
                            className="billing-rates__search"
                            placeholder="Filter category or component"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>

                    {isLoading ? (
                        <p className="billing-rates__hint">Loading rates…</p>
                    ) : error ? (
                        <p className="danger">{error.message || "Failed to load billing rates"}</p>
                    ) : filtered.length === 0 ? (
                        <p className="billing-rates__hint">
                            No rates for this plan as of {asOf}.
                        </p>
                    ) : (
                        <div className="billing-rates__schedules">
                            {filtered.map((plan) => (
                                <ScheduleBlock key={plan.key} plan={plan} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const periodNote = (cat) => {
    const notes = [];
    if (cat.isStatistical) notes.push("Statistical");
    const period = cat.partialPeriodMethodLabel;
    if (period && !/^full\s*period$/i.test(period)) notes.push(period);
    return notes.join(" · ");
};

const coreComponentName = (name) =>
    String(name || "")
        .replace(/^[A-Z][A-Z0-9]{1,7}(?::\s*|\s+)/, "")
        .trim();

const nameAddsDetail = (category, name) => {
    const core = coreComponentName(name).toLowerCase();
    const cat = String(category || "").toLowerCase();
    if (!core || !cat) return false;
    return core !== cat;
};

const flattenRateRows = (plan) => {
    const rows = [];
    for (const cat of plan.categories || []) {
        const category = cat.name || cat.code || "Category";
        const note = periodNote(cat);
        const comps = cat.components || [];
        const push = (comp, entry, { firstInCategory, firstInComponent }) => {
            rows.push({
                key: `${plan.key}-${cat.key}-${comp.key}-${entry?.key ?? "d"}`,
                category,
                note,
                showCategory: firstInCategory,
                name: comp.name || "",
                showName: firstInComponent,
                method: comp.method ? String(comp.method).toUpperCase() : "",
                tier: entry?.tierName || entry?.memberTypeName || "",
                value: amount(
                    entry?.rate ?? comp.rate,
                    entry?.percentage ?? comp.percentage,
                ),
            });
        };
        if (!comps.length) {
            rows.push({
                key: `${plan.key}-${cat.key}-empty`,
                category,
                note,
                showCategory: true,
                name: "",
                showName: false,
                method: "",
                tier: "",
                value: "—",
            });
            continue;
        }
        let catIdx = 0;
        for (const comp of comps) {
            const entries = (comp.entries || []).filter(
                (e) => e.rate != null || e.percentage != null || e.tierName,
            );
            const list = entries.length ? entries : [null];
            list.forEach((entry, entryIdx) => {
                push(comp, entry, {
                    firstInCategory: catIdx === 0,
                    firstInComponent: entryIdx === 0,
                });
                catIdx += 1;
            });
        }
    }
    return rows;
};

function ScheduleBlock({ plan }) {
    const rows = flattenRateRows(plan);
    const methods = [...new Set(rows.map((r) => r.method).filter(Boolean))];
    const showMethod = methods.length > 1;
    const showTier = rows.some((r) => r.tier);
    const showComponent = rows.some((r) => nameAddsDetail(r.category, r.name));
    const meta = [
        plan.planHccId,
        plan.statusLabel || plan.status,
        versionRange(plan.effective, plan.expires),
        methods.length === 1 ? methods[0] : null,
    ].filter((part) => part && part !== "—");

    if (!rows.length) {
        return <p className="billing-rates__hint">No billing categories on this plan.</p>;
    }

    return (
        <section className="billing-rates__plan-schedule">
            <div className="billing-rates__plan-head">
                <div className="billing-rates__plan-name">{plan.planName || "—"}</div>
                {!!meta.length && (
                    <div className="billing-rates__plan-meta">{meta.join(" · ")}</div>
                )}
            </div>
            <div className="table-wrapper billing-rates__table">
                <table>
                    <thead>
                        <tr>
                            <th>Category</th>
                            {showComponent && <th>Component</th>}
                            {showMethod && <th>Method</th>}
                            {showTier && <th>Coverage tier</th>}
                            <th className="billing-rates__num">Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td className="billing-rates__cat">
                                    {r.showCategory ? (
                                        <>
                                            <span>{r.category}</span>
                                            {r.note && (
                                                <span className="billing-rates__cat-note">{r.note}</span>
                                            )}
                                        </>
                                    ) : null}
                                </td>
                                {showComponent && (
                                    <td>
                                        {r.showName && nameAddsDetail(r.category, r.name)
                                            ? r.name
                                            : ""}
                                    </td>
                                )}
                                {showMethod && (
                                    <td className="billing-rates__muted">{r.method}</td>
                                )}
                                {showTier && (
                                    <td className="billing-rates__muted">{r.tier}</td>
                                )}
                                <td className="billing-rates__num">{r.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default BillingRates;
